import { extractSdkErrorDetail } from "@istari-frontend/shared";
import { Button, DialogFooter } from "@istari-frontend/ui";
import { Eye, Info, Pencil, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useRevokeFileFromSendingConnection } from "~/secure-connections/data-source/use-revoke-file-from-sending-connection";
import { useSendingShares } from "~/secure-connections/data-source/use-sending-shares";
import { useShareFileToSendingConnection } from "~/secure-connections/data-source/use-share-file-to-sending-connection";
import { useShareableSendingConnections } from "~/secure-connections/data-source/use-shareable-sending-connections";
import { SharedConnection } from "~/secure-connections/model/connection-share";
import { EditAccess, ShareableSendingConnection } from "~/secure-connections/model/secure-connection";
import { CertificationBox, CertificationCheckbox } from "~/secure-connections/ui/certification-box";
import { ConnectionSearchInput } from "~/secure-connections/ui/connection-search-input";

import {
  OutOfTenantShare,
  OutOfTenantShareRow,
  selectOutOfTenantShares,
  ShareableSendingConnectionRow,
} from "./share-rows";

type SendingShareViewProps = {
  // The resource id (model/artifact) — used for the shares list endpoint.
  resourceId: string;
  // The underlying file id from the resource — used for the share/revoke mutation endpoints.
  fileId: string;
  // Noun phrase for the shared thing, e.g. "this model" or "this artifact". Partners gain
  // access to a model or artifact, not to its underlying file.
  subject: string;
  onClose: () => void;
  onUpdateComplete?: () => void;
  labelExtra?: ReactNode;
};

export function SendingShareView({
  resourceId,
  fileId,
  subject,
  onClose,
  onUpdateComplete,
  labelExtra,
}: SendingShareViewProps) {
  const connectionsQuery = useShareableSendingConnections();
  const sharesQuery = useSendingShares(resourceId);
  const shareMutation = useShareFileToSendingConnection();
  const revokeMutation = useRevokeFileFromSendingConnection();

  const allConnections = useMemo(() => connectionsQuery.data ?? [], [connectionsQuery.data]);

  const [selectedLevels, setSelectedLevels] = useState<Map<string, EditAccess>>(new Map());
  const [certifyView, setCertifyView] = useState(false);
  const [certifyEdit, setCertifyEdit] = useState(false);
  const [certifyRemove, setCertifyRemove] = useState(false);
  const originalLevelsRef = useRef<Map<string, EditAccess>>(new Map());
  const hasSeededRef = useRef(false);

  // Seed local selection from the server shares once the list arrives.
  useEffect(() => {
    if (hasSeededRef.current || !sharesQuery.data) return;

    const levels = new Map<string, EditAccess>(
      sharesQuery.data.map((share) => [share.sendingConnectionId, share.editAccess]),
    );
    originalLevelsRef.current = levels;
    setSelectedLevels(new Map(levels));
    hasSeededRef.current = true;
  }, [sharesQuery.data]);

  const sharedConnections = useMemo(
    () => allConnections.filter((c) => selectedLevels.has(c.id)),
    [allConnections, selectedLevels],
  );
  const outOfTenantShares = useMemo(
    () => selectOutOfTenantShares(sharesQuery.data, connectionsQuery.data),
    [sharesQuery.data, connectionsQuery.data],
  );
  const listedOutOfTenantShares = useMemo(
    () => [...outOfTenantShares.values()].filter((share) => selectedLevels.has(share.id)),
    [outOfTenantShares, selectedLevels],
  );
  const availableConnections = useMemo(
    () => allConnections.filter((c) => !selectedLevels.has(c.id)),
    [allConnections, selectedLevels],
  );

  const { addedIds, removedIds, changedIds } = useMemo(() => {
    const added: string[] = [];
    const removed: string[] = [];
    const changed: string[] = [];
    for (const [id, level] of selectedLevels) {
      const original = originalLevelsRef.current.get(id);
      if (original === undefined) added.push(id);
      else if (original !== level) changed.push(id);
    }
    for (const id of originalLevelsRef.current.keys()) {
      if (!selectedLevels.has(id)) removed.push(id);
    }
    return { addedIds: added, removedIds: removed, changedIds: changed };
  }, [selectedLevels]);

  const hasRemovedOrgs = removedIds.length > 0;
  const hasAnyChange = addedIds.length > 0 || hasRemovedOrgs || changedIds.length > 0;
  const confirmUsesSaveLabel = hasRemovedOrgs || changedIds.length > 0;

  // Partition the orgs whose final state needs a fresh acknowledgment.
  const connectionsById = new Map<string, SharedConnection>(
    [...allConnections, ...outOfTenantShares.values()].map((c) => [c.id, c]),
  );
  const resolveConnections = (ids: string[]) =>
    ids.map((id) => connectionsById.get(id)).filter((c): c is SharedConnection => c !== undefined);
  const changedAndAdded = resolveConnections([...addedIds, ...changedIds]);
  const viewLevelPartners = changedAndAdded.filter((c) => (selectedLevels.get(c.id) ?? "viewer") !== "editor");
  const editLevelPartners = changedAndAdded.filter((c) => selectedLevels.get(c.id) === "editor");
  const removedPartners = resolveConnections(removedIds);

  const needsCertifyView = viewLevelPartners.length > 0 && !certifyView;
  const needsCertifyEdit = editLevelPartners.length > 0 && !certifyEdit;
  const needsCertifyRemove = removedPartners.length > 0 && !certifyRemove;

  const isPending = shareMutation.isPending || revokeMutation.isPending;
  const isInitialLoading = sharesQuery.isLoading || connectionsQuery.isLoading;
  // Block save while connection/share data failed to load — the diff can't be trusted.
  const isLoadError = connectionsQuery.isError || sharesQuery.isError;

  // Certification approves the exact membership of each bucket, so any change to a
  // bucket's contents invalidates its acknowledgment. The operator must re-tick
  // rather than carry over consent given for a different set of partners.

  // New shares default to viewer — the safer choice. The user opts up to editor
  // explicitly via the per-row selector when the connection allows it.
  const handleAdd = (connection: ShareableSendingConnection) => {
    setSelectedLevels((prev) => new Map(prev).set(connection.id, "viewer"));
    setCertifyView(false);
  };

  const handleRemove = (connectionId: string) => {
    setSelectedLevels((prev) => {
      const next = new Map(prev);
      next.delete(connectionId);
      return next;
    });
    setCertifyRemove(false);
  };

  // A level change shuffles partners between the view/edit certify buckets, so
  // force both checkboxes to be re-ticked.
  const handleLevelChange = (connectionId: string, level: EditAccess) => {
    setSelectedLevels((prev) => new Map(prev).set(connectionId, level));
    setCertifyView(false);
    setCertifyEdit(false);
  };

  const handleSave = async () => {
    const writes = [...addedIds, ...changedIds].map((connectionId) => ({
      connectionId,
      run: () =>
        shareMutation.mutateAsync({
          connectionId,
          fileId,
          resourceId,
          editAccess: selectedLevels.get(connectionId) ?? "viewer",
        }),
    }));
    const revokes = removedIds.map((connectionId) => ({
      connectionId,
      run: () => revokeMutation.mutateAsync({ connectionId, fileId, resourceId }),
    }));
    const operations = [...writes, ...revokes];

    // allSettled (not Promise.all) so one failure doesn't mask the rest. On any
    // failure, re-seed against fresh server state on the next shares fetch — the
    // baseline diff is now stale, and retrying off it would re-issue completed ops.
    const results = await Promise.allSettled(operations.map((op) => op.run()));
    const failures = results.flatMap((result, index) =>
      result.status === "rejected" ? [{ connectionId: operations[index].connectionId, reason: result.reason }] : [],
    );

    if (failures.length === 0) {
      toast.success("External connection sharing updated.");
      onClose();
      onUpdateComplete?.();
      return;
    }

    hasSeededRef.current = false;
    const failedNames = failures
      .map(({ connectionId }) => connectionsById.get(connectionId)?.label ?? connectionId)
      .join(", ");

    // Surface the server's rejection reason (e.g. an infosec-level mismatch) when present —
    // the name list alone doesn't tell the user why the share failed. Dedupe since multiple
    // partners often fail for the same reason.
    const details = [
      ...new Set(
        (await Promise.all(failures.map(({ reason }) => extractSdkErrorDetail(reason)))).filter(
          (detail): detail is string => Boolean(detail),
        ),
      ),
    ];
    const reasonSuffix = details.length > 0 ? ` ${details.join(" ")}` : "";
    toast.error(`Failed to update sharing for: ${failedNames}.${reasonSuffix}`);
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-visible">
        <ConnectionSearchInput
          availableConnections={availableConnections}
          onSelectConnection={handleAdd}
          isLoading={isInitialLoading}
          disabled={isPending || isLoadError}
          labelExtra={labelExtra}
        />

        <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
          {isLoadError ? (
            <div className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
              Failed to load external connections. Close this dialog and try again.
            </div>
          ) : (
            <SharedConnectionsList
              sharedConnections={sharedConnections}
              outOfTenantShares={listedOutOfTenantShares}
              selectedLevels={selectedLevels}
              onRemove={handleRemove}
              onLevelChange={handleLevelChange}
              disabled={isPending}
            />
          )}

          <CertificationBlock
            subject={subject}
            viewLevelPartners={viewLevelPartners}
            editLevelPartners={editLevelPartners}
            removedPartners={removedPartners}
            certifyView={certifyView}
            certifyEdit={certifyEdit}
            certifyRemove={certifyRemove}
            onCertifyViewChange={setCertifyView}
            onCertifyEditChange={setCertifyEdit}
            onCertifyRemoveChange={setCertifyRemove}
          />

          <div className="border-border bg-muted/40 text-muted-foreground flex items-start gap-2 rounded-md border p-3 text-sm leading-snug">
            <Info className="size-4 shrink-0" aria-hidden />
            <span>Sharing with Secure Connections does not cascade; child resources must be manually shared.</span>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            !hasAnyChange ||
            needsCertifyView ||
            needsCertifyEdit ||
            needsCertifyRemove ||
            isPending ||
            isInitialLoading ||
            isLoadError
          }
        >
          {confirmActionLabel({ isPending, usesSaveLabel: confirmUsesSaveLabel })}
        </Button>
      </DialogFooter>
    </>
  );
}

function confirmActionLabel({ isPending, usesSaveLabel }: { isPending: boolean; usesSaveLabel: boolean }): string {
  if (isPending) return usesSaveLabel ? "Saving..." : "Sharing...";
  return usesSaveLabel ? "Save" : "Share";
}

function SharedConnectionsList({
  sharedConnections,
  outOfTenantShares,
  selectedLevels,
  onRemove,
  onLevelChange,
  disabled,
}: {
  sharedConnections: ShareableSendingConnection[];
  outOfTenantShares: OutOfTenantShare[];
  selectedLevels: Map<string, EditAccess>;
  onRemove: (connectionId: string) => void;
  onLevelChange: (connectionId: string, level: EditAccess) => void;
  disabled: boolean;
}) {
  const count = sharedConnections.length + outOfTenantShares.length;

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">
        {count === 0 ? "No connections with access" : `Connections with access (${count})`}
      </div>

      {count === 0 ? (
        <div className="flex h-[100px] items-center justify-center rounded-md border">
          <p className="text-muted-foreground text-sm">Select an organization above to share access</p>
        </div>
      ) : (
        <div className="rounded-md border">
          {sharedConnections.map((connection) => (
            <ShareableSendingConnectionRow
              key={connection.id}
              connection={connection}
              shareLevel={selectedLevels.get(connection.id) ?? "viewer"}
              onLevelChange={(level) => onLevelChange(connection.id, level)}
              onRemove={() => onRemove(connection.id)}
              disabled={disabled}
            />
          ))}
          {outOfTenantShares.map((share) => (
            <OutOfTenantShareRow key={share.id} share={share} onRemove={() => onRemove(share.id)} disabled={disabled} />
          ))}
        </div>
      )}
    </div>
  );
}

function CertificationBlock({
  subject,
  viewLevelPartners,
  editLevelPartners,
  removedPartners,
  certifyView,
  certifyEdit,
  certifyRemove,
  onCertifyViewChange,
  onCertifyEditChange,
  onCertifyRemoveChange,
}: {
  subject: string;
  viewLevelPartners: SharedConnection[];
  editLevelPartners: SharedConnection[];
  removedPartners: SharedConnection[];
  certifyView: boolean;
  certifyEdit: boolean;
  certifyRemove: boolean;
  onCertifyViewChange: (checked: boolean) => void;
  onCertifyEditChange: (checked: boolean) => void;
  onCertifyRemoveChange: (checked: boolean) => void;
}) {
  const hasAnyCert = viewLevelPartners.length > 0 || editLevelPartners.length > 0 || removedPartners.length > 0;
  if (!hasAnyCert) return null;

  return (
    <CertificationBox>
      {viewLevelPartners.length > 0 && (
        <ShareCertCheckbox
          kind="view"
          subject={subject}
          partners={viewLevelPartners}
          checked={certifyView}
          onCheckedChange={onCertifyViewChange}
        />
      )}
      {editLevelPartners.length > 0 && (
        <ShareCertCheckbox
          kind="edit"
          subject={subject}
          partners={editLevelPartners}
          checked={certifyEdit}
          onCheckedChange={onCertifyEditChange}
        />
      )}
      {removedPartners.length > 0 && (
        <ShareCertCheckbox
          kind="remove"
          subject={subject}
          partners={removedPartners}
          checked={certifyRemove}
          onCheckedChange={onCertifyRemoveChange}
        />
      )}
    </CertificationBox>
  );
}

type ShareCertKind = "view" | "edit" | "remove";

function ShareCertCheckbox({
  kind,
  subject,
  partners,
  checked,
  onCheckedChange,
}: {
  kind: ShareCertKind;
  subject: string;
  partners: SharedConnection[];
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const copy = certCopy(kind, subject);
  return (
    <div className="text-foreground space-y-2" data-testid={`share-cert-${kind}`}>
      <div className="space-y-1">
        <div className="text-sm">{copy.heading}</div>
        <ul className="space-y-0.5 text-sm">
          {partners.map((partner) => (
            <li key={partner.id} className="flex items-center gap-1.5">
              <CertIcon kind={kind} />
              {partner.label}
            </li>
          ))}
        </ul>
      </div>
      <CertificationCheckbox checked={checked} onCheckedChange={onCheckedChange}>
        {copy.label}
      </CertificationCheckbox>
    </div>
  );
}

function CertIcon({ kind }: { kind: ShareCertKind }) {
  if (kind === "edit") return <Pencil className="size-3.5 shrink-0" />;
  if (kind === "view") return <Eye className="size-3.5 shrink-0" />;
  return <X className="size-3.5 shrink-0" />;
}

function certCopy(kind: ShareCertKind, subject: string): { heading: ReactNode; label: ReactNode } {
  if (kind === "edit") {
    return {
      heading: (
        <>
          These External Organization(s) will gain <strong className="font-semibold">edit</strong> access:
        </>
      ),
      label: (
        <>
          Confirm these External Organization(s) will be able to run jobs and{" "}
          <strong className="font-semibold">edit</strong> their copy of {subject}.
        </>
      ),
    };
  }
  if (kind === "view") {
    return {
      heading: (
        <>
          These External Organization(s) will gain <strong className="font-semibold">view</strong> access:
        </>
      ),
      label: (
        <>
          Confirm these External Organization(s) will gain <strong className="font-semibold">view</strong> access to{" "}
          {subject}.
        </>
      ),
    };
  }
  return {
    heading: "These External Organization(s) will lose access:",
    label: `Confirm these External Organization(s) will lose access to ${subject}. A revocation request will be sent to their instance to lock it.`,
  };
}
